import { useEffect, useMemo, useState } from "react";
import { Logo } from "../../components/dashboard/Logo";
import { Skeleton } from "../../components/dashboard/Skeleton";
import { useAdminProviders } from "../../hooks/useAdminProviders";
import { providerLabel } from "../../lib/providerLabel";
import type { AdminProviderRow } from "../../types/adminProvider";
import styles from "./IntegrationCatalogSection.module.css";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";

// IP-222: the catalog breakdown that sits below the platform-authentication
// section on the admin Integrations tab. A read-only, filterable view of every
// provider implementation so we can see at a glance how many apps we have and
// which are Composio-managed / enabled / by-auth-type.
//
// Vocabulary: a "provider" is the upstream broker (Composio, ServiceM8, ...),
// of which there are only a handful. An "integration" is an app — counted by
// distinct slug, so a logical app offered through >1 provider is still one
// integration. The table itself lists one row per provider implementation.

const PAGE_SIZE = 50;

const isComposio = (row: AdminProviderRow): boolean => row.provider === "composio";

// null (no auth_type recorded) and the explicit "no_auth" scheme both mean the
// integration needs no authentication — treated as one "None" bucket.
const isNoAuth = (authType: string | null): boolean =>
  authType === null || authType === "no_auth";

const AUTH_LABEL: Record<string, string> = {
  oauth2: "OAuth2",
  api_key: "API key",
};

const authLabel = (authType: string | null): string =>
  authType === null ? "—" : (AUTH_LABEL[authType] ?? authType);


// Managed-auth posture. Keyed on the composio_managed column (null = n/a /
// not applicable to this provider), NOT on the provider name, so a future
// managed-auth broker flows through without a code change.
const managedLabel = (row: AdminProviderRow): string => {
  if (row.composioManaged === null) return "—";
  return row.composioManaged ? "Managed" : "BYO Auth";
};

const syncedLabel = (iso: string | null): string => {
  if (iso === null) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

type Summary = {
  // Total integrations = one per provider row (each app, through each broker).
  // Kept as the row count by product decision so it matches the table total and
  // the "every Composio app + ServiceM8" framing, even though a logical app
  // offered through >1 provider is then counted per-provider.
  integrations: number;
  // Distinct brokers (Composio, ServiceM8, ...) — a small number.
  providerCount: number;
  // Integrations per broker, biggest first.
  byProvider: ReadonlyArray<{ provider: string; count: number }>;
  // Distinct non-null auth types, excluding no_auth (folded into "None"),
  // sorted — the data-driven Auth filter options.
  authTypes: ReadonlyArray<string>;
  oauth: number;
  apiKey: number;
  otherAuth: number;
  noAuth: number;
  connectable: number;
};

const summarise = (rows: ReadonlyArray<AdminProviderRow>): Summary => {
  const perProvider = new Map<string, number>();
  const authSet = new Set<string>();
  let oauth = 0;
  let apiKey = 0;
  let otherAuth = 0;
  let noAuth = 0;
  let connectable = 0;
  for (const r of rows) {
    perProvider.set(r.provider, (perProvider.get(r.provider) ?? 0) + 1);
    if (isNoAuth(r.authType)) noAuth += 1;
    else if (r.authType === "oauth2") oauth += 1;
    else if (r.authType === "api_key") apiKey += 1;
    else otherAuth += 1;
    if (r.authType !== null && !isNoAuth(r.authType)) authSet.add(r.authType);
    if (r.configured) connectable += 1;
  }
  const byProvider = Array.from(perProvider.entries())
    .map(([provider, count]) => ({ provider, count }))
    .sort((a, b) => b.count - a.count);
  return {
    integrations: rows.length,
    providerCount: perProvider.size,
    byProvider,
    authTypes: Array.from(authSet).sort(),
    oauth,
    apiKey,
    otherAuth,
    noAuth,
    connectable,
  };
};

const StatCard = ({
  label,
  value,
  rows,
}: {
  label: string;
  value?: number;
  rows?: ReadonlyArray<{ k: string; v: number }>;
}): JSX.Element => (
  <div className={styles.statCard}>
    <span className={styles.statLabel}>{label}</span>
    {value !== undefined ? (
      <span className={styles.statValue}>{value.toLocaleString()}</span>
    ) : (
      <div className={styles.statRows}>
        {rows?.map((r) => (
          <div key={r.k} className={styles.statRow}>
            <span className={styles.statRowK}>{r.k}</span>
            <span className={styles.statRowV}>{r.v.toLocaleString()}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const IntegrationCatalogSection = (): JSX.Element => {
  const [includeInactive, setIncludeInactive] = useState(false);
  const { status, providers, error, isFetching, refetch } = useAdminProviders(includeInactive);

  const [providerFilter, setProviderFilter] = useState("all");
  const [authFilter, setAuthFilter] = useState("all");
  const [connectableFilter, setConnectableFilter] = useState<"all" | "yes" | "no">("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  // Summary reflects the full server payload (active, or active+inactive when
  // toggled) — not the client filters — so the totals stay a stable headline.
  const summary = useMemo(() => summarise(providers), [providers]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return providers.filter((r) => {
      if (providerFilter !== "all" && r.provider !== providerFilter) return false;
      if (authFilter === "none") {
        if (!isNoAuth(r.authType)) return false;
      } else if (authFilter !== "all" && r.authType !== authFilter) {
        return false;
      }
      if (connectableFilter === "yes" && !r.configured) return false;
      if (connectableFilter === "no" && r.configured) return false;
      if (
        q !== "" &&
        !r.title.toLowerCase().includes(q) &&
        !r.slug.toLowerCase().includes(q) &&
        !r.providerAppId.toLowerCase().includes(q)
      ) {
        return false;
      }
      return true;
    });
  }, [providers, providerFilter, authFilter, connectableFilter, query]);

  // Any change to the filter set (or a server refetch) returns to page 1.
  useEffect(() => {
    setPage(1);
  }, [providerFilter, authFilter, connectableFilter, query, includeInactive]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // Clamp at render so a shrinking result set can't strand us past the end.
  const safePage = Math.min(page, pageCount);
  const start = filtered.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const end = Math.min(safePage * PAGE_SIZE, filtered.length);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const anyFilterActive =
    includeInactive ||
    providerFilter !== "all" ||
    authFilter !== "all" ||
    connectableFilter !== "all" ||
    query.trim() !== "";

  const clearFilters = (): void => {
    setProviderFilter("all");
    setAuthFilter("all");
    setConnectableFilter("all");
    setQuery("");
    setIncludeInactive(false);
  };

  return (
    <section className={styles.section}>
      <AdminPageIntro title="Integration catalog">
        Every app we offer, the broker it comes through and its auth type. Read-only.
      </AdminPageIntro>

      <div className={styles.summary}>
        <StatCard label="Integrations" value={summary.integrations} />
        <StatCard label="Providers" value={summary.providerCount} />
        <StatCard
          label="By provider"
          rows={summary.byProvider.map((p) => ({ k: providerLabel(p.provider), v: p.count }))}
        />
        <StatCard
          label="By auth type"
          rows={[
            { k: "OAuth2", v: summary.oauth },
            { k: "API key", v: summary.apiKey },
            { k: "Other", v: summary.otherAuth },
            { k: "None", v: summary.noAuth },
          ]}
        />
        <StatCard
          label="Enabled"
          rows={[
            { k: "Yes", v: summary.connectable },
            { k: "No", v: providers.length - summary.connectable },
          ]}
        />
      </div>

      <div className={styles.toolbar}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Provider</span>
          <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
            <option value="all">All</option>
            {summary.byProvider.map((p) => (
              <option key={p.provider} value={p.provider}>
                {providerLabel(p.provider)}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Auth type</span>
          <select value={authFilter} onChange={(e) => setAuthFilter(e.target.value)}>
            <option value="all">All</option>
            {summary.authTypes.map((a) => (
              <option key={a} value={a}>
                {authLabel(a)}
              </option>
            ))}
            <option value="none">None</option>
          </select>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Enabled</span>
          <select
            value={connectableFilter}
            onChange={(e) => setConnectableFilter(e.target.value as "all" | "yes" | "no")}
          >
            <option value="all">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </label>

        <label className={`${styles.field} ${styles.searchField}`}>
          <span className={styles.fieldLabel}>Search</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Title, slug, or app id"
          />
        </label>

        <div className={styles.toolbarRight}>
          <button
            type="button"
            className={`${styles.chip} ${includeInactive ? styles.chipActive : ""}`}
            onClick={() => setIncludeInactive((v) => !v)}
          >
            Show inactive
          </button>
          {anyFilterActive && (
            <button type="button" className={styles.linkBtn} onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {status === "error" ? (
        <div className={styles.errorBox}>
          <span>{error ?? "Failed to load the catalog."}</span>
          <button type="button" className={styles.linkBtn} onClick={() => void refetch()}>
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className={styles.countMeta}>
            {status === "loading"
              ? "Loading…"
              : filtered.length === 0
                ? "No integrations match these filters"
                : `Showing ${start.toLocaleString()}-${end.toLocaleString()} of ${filtered.length.toLocaleString()} integrations`}
          </div>
          <div
            className={
              status === "ready" && isFetching
                ? `${styles.tableWrap} ${styles.tableFetching}`
                : styles.tableWrap
            }
          >
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>App</th>
                  <th className={styles.center}>Provider</th>
                  <th className={styles.center} title="Auth type, and whether Composio manages it or we bring our own">
                    Auth
                  </th>
                  <th className={styles.center}>Enabled</th>
                  <th className={styles.center}>Tools</th>
                  <th className={styles.center}>Lifecycle</th>
                  <th className={styles.center}>Synced</th>
                </tr>
              </thead>
              <tbody>
                {status === "loading" ? (
                  Array.from({ length: 8 }, (_, row) => (
                    <tr key={row} aria-hidden>
                      {Array.from({ length: 7 }, (_, col) => (
                        <td key={col}>
                          <Skeleton height="0.9375rem" width={col === 0 ? "72%" : "48%"} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : status === "ready" && filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className={styles.empty}>
                      No integrations match these filters.
                    </td>
                  </tr>
                ) : (
                  paged.map((r) => (
                    <tr key={`${r.provider}:${r.providerAppId}`}>
                      <td>
                        <div className={styles.appCell}>
                          <Logo slug={r.slug} size={26} logoUrl={r.logoUrl} />
                          <div className={styles.appText}>
                            <span className={styles.appTitle}>{r.title}</span>
                            <span className={styles.mono}>{r.slug}</span>
                          </div>
                        </div>
                      </td>
                      <td className={styles.center}>
                        <span
                          className={`${styles.badge} ${isComposio(r) ? styles.badgeComposio : styles.badgeNative}`}
                        >
                          {r.provider}
                        </span>
                      </td>
                      <td className={styles.center}>
                        <div className={styles.stackCenter}>
                          <span className={styles.badge}>{authLabel(r.authType)}</span>
                          {r.composioManaged !== null && <span className={styles.subtle}>{managedLabel(r)}</span>}
                        </div>
                      </td>
                      <td className={styles.center}>
                        <span
                          className={`${styles.pill} ${r.configured ? styles.pillYes : styles.pillNo}`}
                          title={
                            r.configuredOverride !== null
                              ? `Admin override: forced ${r.configuredOverride ? "on" : "off"}`
                              : undefined
                          }
                        >
                          {r.configured ? "Yes" : "No"}
                          {r.configuredOverride !== null ? " *" : ""}
                        </span>
                      </td>
                      <td className={`${styles.muted} ${styles.center}`}>{r.toolCount ?? "—"}</td>
                      <td className={styles.center}>
                        <span className={`${styles.pill} ${styles[`life_${r.lifecycleStatus}`] ?? ""}`}>
                          {r.lifecycleStatus}
                        </span>
                      </td>
                      <td className={`${styles.muted} ${styles.center}`}>{syncedLabel(r.lastSyncedAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {status === "ready" && filtered.length > PAGE_SIZE && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageBtn}
                onClick={() => setPage(safePage - 1)}
                disabled={safePage <= 1}
              >
                Prev
              </button>
              <span className={styles.pageMeta}>
                Page {safePage.toLocaleString()} of {pageCount.toLocaleString()}
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                onClick={() => setPage(safePage + 1)}
                disabled={safePage >= pageCount}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};
